import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { ensureTaxRepositoryConfigured } from "@/lib/taxRepository";
import { PageHeader } from "@/pages/layoutChrome";
import { IdentityValidateButton } from "@/components/identity/IdentityCheckDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ChevronDown,
  Copy,
  FileSpreadsheet,
  FileText,
  FileUp,
  Globe,
  HelpCircle,
  Loader2,
  Search,
  Users,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  downloadLaboralReport,
  formatFechaValidacionLocal,
  laboralExportErrorMessage,
  MSG_SIN_DATOS_EXPORTAR,
} from "@/lib/laboralReportExport";
import { toast } from "sonner";
import LaboralFlujoOperativo from "@/components/laboral/LaboralFlujoOperativo";
import PortalExternoGuiaPanel from "@/pages/laboral/PortalExternoGuiaPanel";

const IDSE_PORTAL_DEFAULT = "https://idse.imss.gob.mx/";
const IMSS_PUBLIC_DEFAULT =
  "https://serviciosdigitales.imss.gob.mx/portal-ciudadano-web-externo/asegurados/tramite/actualizacion/nss";

function nssBadge(status) {
  const s = (status || "pendiente").toLowerCase();
  if (s === "localizado") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s === "no_localizado") return "bg-red-100 text-red-800 border-red-200";
  return "bg-amber-50 text-amber-900 border-amber-200";
}

function nssLabel(status) {
  const s = (status || "pendiente").toLowerCase();
  if (s === "localizado") return "NSS localizado";
  if (s === "no_localizado") return "No localizado";
  return "Pendiente";
}

async function copyText(value, label) {
  if (!value) {
    toast.error(`No hay ${label} para copiar`);
    return;
  }
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copiado al portapapeles`);
  } catch {
    toast.error(`No se pudo copiar ${label}`);
  }
}

/**
 * Validador de NSS (IMSS) — flujo unificado IDSE + Plan B portal público.
 */
export default function ValidadorNssImss() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [config, setConfig] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [selectAllPending, setSelectAllPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastLayoutPath, setLastLayoutPath] = useState("");
  const [resultados, setResultados] = useState(null);
  const [respuestaFile, setRespuestaFile] = useState(null);
  const [planBOpen, setPlanBOpen] = useState(false);
  const [portalMode, setPortalMode] = useState("idse");

  const cli = useMemo(() => clients.find((c) => c.id === clientId), [clients, clientId]);
  const selected = useMemo(
    () => workers.find((w) => w.id === selectedId) || null,
    [workers, selectedId],
  );

  const idseUrl = config?.idse_portal_url || IDSE_PORTAL_DEFAULT;
  const publicUrl = config?.imss_portal_publico_url || IMSS_PUBLIC_DEFAULT;
  const portalUrl = portalMode === "publico" ? publicUrl : idseUrl;
  const instrucciones = config?.instrucciones || "";
  const notaPlanB = config?.nota_plan_b || "";

  const workersByCurp = useMemo(() => {
    const map = new Map();
    workers.forEach((w) => {
      const key = (w.curp || "").trim().toUpperCase();
      if (key) map.set(key, w);
    });
    return map;
  }, [workers]);

  const filasExportacionNss = useMemo(() => {
    if (!resultados?.items?.length) return [];
    return resultados.items.map((row) => {
      const w = workersByCurp.get((row.curp || "").trim().toUpperCase());
      return {
        nombre_completo: row.nombre_completo || w?.nombre_completo || "",
        curp: row.curp || "",
        rfc: row.rfc || w?.rfc || "",
        nss: row.nss || "",
        estatus: row.estatus || "",
      };
    });
  }, [resultados, workersByCurp]);

  const onExportarNss = async (formato) => {
    if (!clientId) {
      toast.error("Seleccione un cliente");
      return;
    }
    if (!filasExportacionNss.length) {
      toast.warning(MSG_SIN_DATOS_EXPORTAR);
      return;
    }
    setExporting(true);
    try {
      const ext = formato === "pdf" ? "pdf" : "xlsx";
      const filename = await downloadLaboralReport({
        endpoint: "/laboral/validacion-nss/exportar-reporte",
        payload: {
          client_id: clientId,
          formato,
          fecha_validacion: formatFechaValidacionLocal(),
          items: filasExportacionNss,
        },
        fallbackFilename: `reporte_estatus_nss.${ext}`,
      });
      toast.success(`Reporte descargado: ${filename}`);
    } catch (e) {
      toast.error(await laboralExportErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const loadWorkers = useCallback(async () => {
    if (!clientId) {
      setWorkers([]);
      return;
    }
    try {
      const { data } = await api.get("/laboral/trabajadores-registro", {
        params: { client_id: clientId, q: searchQ.trim() || undefined },
      });
      setWorkers(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setWorkers([]);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar trabajadores");
    }
  }, [clientId, searchQ]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (!clientId) {
      setConfig(null);
      setSelectedId("");
      return;
    }
    api
      .get("/laboral/validacion-nss/config", { params: { client_id: clientId } })
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig(null));
  }, [clientId]);

  useEffect(() => {
    const t = setTimeout(() => loadWorkers(), searchQ ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadWorkers, searchQ]);

  useEffect(() => {
    setSelectedId("");
    setSelectAllPending(false);
    setLastLayoutPath("");
    setResultados(null);
    setRespuestaFile(null);
    setPlanBOpen(false);
    setPortalMode("idse");
  }, [clientId]);

  useEffect(() => {
    if (selectAllPending) setSelectedId("");
  }, [selectAllPending]);

  useEffect(() => {
    if (selectedId) setSelectAllPending(false);
  }, [selectedId]);

  const onGenerarConsulta = async () => {
    if (!clientId) {
      toast.error("Seleccione un cliente");
      return;
    }
    if (!selectAllPending && !selectedId) {
      toast.error("Seleccione un trabajador o «Todos los pendientes de NSS»");
      return;
    }
    setBusy(true);
    try {
      await ensureTaxRepositoryConfigured({
        clientId,
        dateStr: new Date().toISOString().slice(0, 10),
        direccion: "emitido",
      });
      const body = new FormData();
      body.append("client_id", clientId);
      if (selectAllPending) {
        body.append("todos_pendientes", "true");
      } else {
        body.append("trabajador_id", selectedId);
      }
      const { data } = await api.post("/laboral/validacion-nss/generar-consulta-idse", body);
      const path = data.file_path || "";
      if (path) {
        try {
          await navigator.clipboard.writeText(path);
          toast.success("Ruta del archivo copiada al portapapeles");
        } catch {
          toast.info("Archivo generado; copie la ruta del recuadro inferior.");
        }
      }
      setLastLayoutPath(path);
      toast.success(data.message || `Consulta IDSE generada (${data.line_count} CURP)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar el archivo");
    } finally {
      setBusy(false);
    }
  };

  const onProcesarRespuesta = async () => {
    if (!clientId || !respuestaFile) {
      toast.error("Seleccione el archivo .txt de respuesta IDSE o EMA");
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      body.append("archivo_respuesta", respuestaFile);
      const { data } = await api.post("/laboral/validacion-nss/procesar-respuesta", body);
      setResultados(data);
      toast.success(data.message || "Respuesta procesada");
      await loadWorkers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo procesar el archivo");
    } finally {
      setBusy(false);
    }
  };

  const gridRows = resultados?.items?.length
    ? resultados.items
    : workers.filter((w) => w.nss || w.validacion_nss_status === "localizado").slice(0, 50);

  const portalGuiaSteps = useMemo(() => {
    if (portalMode === "publico") {
      return [
        {
          key: "abrir",
          title: "Abrir portal público IMSS",
          description: "Use el botón verde. Requiere CURP y correo histórico del trabajador (Plan B).",
        },
        {
          key: "copiar",
          title: "Copiar datos del trabajador",
          description: "Desde el panel izquierdo o Plan B, copie CURP y correo y complete el trámite en IMSS Digital.",
        },
        {
          key: "volver",
          title: "Registrar NSS en Visor Fiscal",
          description: "Si localizó el NSS manualmente, actualícelo en el catálogo de trabajadores o use el flujo IDSE cuando esté disponible.",
        },
      ];
    }
    return [
      {
        key: "abrir",
        title: "Abrir IDSE con certificado patronal",
        description: "Inicie sesión en idse.imss.gob.mx con .cer/.key del registro patronal (no desde esta app).",
      },
      {
        key: "subir",
        title: "Cargar archivo de consulta",
        description: "Suba el .txt generado en el paso 1 del panel izquierdo en la consulta masiva IDSE.",
      },
      {
        key: "respuesta",
        title: "Procesar respuesta aquí",
        description: "Descargue la respuesta IDSE o archivo EMA y procéselo en «Procesar Respuesta IDSE».",
      },
    ];
  }, [portalMode]);

  return (
    <div className="min-h-screen flex flex-col">
      <PageHeader
        title="Validador de NSS (IMSS)"
        subtitle="Localice NSS por IDSE (individual o lote) con respaldo portal público."
        breadcrumb={["Asistente Laboral y REPSE", "Validador de NSS"]}
      />

      <LaboralFlujoOperativo>
      <div
        className="flex-1 grid grid-cols-1 xl:grid-cols-2 min-h-0 border-t border-slate-200"
        data-testid="validador-nss-split"
      >
        {/* Panel izquierdo — flujo IDSE continuo */}
        <div className="overflow-y-auto p-4 md:p-6 space-y-4 bg-[#F4F6F8] border-b xl:border-b-0 xl:border-r border-slate-200">
          {instrucciones ? (
            <div className="rounded-md border border-sky-200 bg-sky-50/90 px-3 py-3 text-xs text-slate-800 leading-relaxed flex gap-2">
              <HelpCircle className="w-4 h-4 shrink-0 text-sky-700 mt-0.5" />
              <p className="m-0 whitespace-pre-line">{instrucciones}</p>
            </div>
          ) : null}

          <div className="bg-white rounded-md border border-slate-200 p-4 space-y-3">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Cliente (patrón)</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="nss-client-select" className="h-10">
                <SelectValue placeholder="Seleccione cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.razon_social} — {c.rfc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sección 1 — Selección */}
          <div className="bg-white rounded-md border border-slate-200 p-4 space-y-3">
            <Label className="text-xs font-semibold text-[#0F2D5C]">1. Selección de datos</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="CURP, NSS o nombre…"
                className="pl-8 h-9 text-xs"
                disabled={!clientId || selectAllPending}
              />
            </div>
            <Select
              value={selectedId}
              onValueChange={setSelectedId}
              disabled={!clientId || selectAllPending || !workers.length}
            >
              <SelectTrigger className="h-10 text-xs">
                <SelectValue placeholder={workers.length ? "Consulta individual (1 trabajador)" : "Sin registros"} />
              </SelectTrigger>
              <SelectContent>
                {workers.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {(w.nombre_completo || w.curp || w.id).slice(0, 50)}
                    {" — "}
                    <span className="font-mono">{w.curp || "—"}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant={selectAllPending ? "default" : "outline"}
              className={`w-full h-10 text-xs justify-start ${selectAllPending ? "bg-[#0F2D5C] hover:bg-[#0a2247]" : ""}`}
              disabled={!clientId || busy}
              onClick={() => setSelectAllPending((v) => !v)}
              data-testid="btn-todos-pendientes-nss"
            >
              <Users className="w-4 h-4 mr-2 shrink-0" />
              {selectAllPending ? "✓ Todos los pendientes de NSS seleccionados" : "Seleccionar todos los pendientes de NSS"}
            </Button>
            {selected && !selectAllPending ? (
              <p className="text-[11px] text-slate-500 m-0 font-mono">
                CURP: {selected.curp || "—"} · NSS actual: {selected.nss || "pendiente"}
              </p>
            ) : null}
          </div>

          {/* Sección 2 — Acciones IDSE */}
          <div className="bg-white rounded-md border border-slate-200 p-4 space-y-3">
            <Label className="text-xs font-semibold text-[#0F2D5C]">2. Acciones IDSE</Label>
            <Button
              type="button"
              disabled={busy || !clientId}
              onClick={onGenerarConsulta}
              className="w-full h-12 justify-start font-bold bg-[#0F2D5C] hover:bg-[#0a2247]"
              data-testid="btn-generar-consulta-idse"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (
                <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-white/20 text-xs mr-2">1</span>
              )}
              Generar Archivo de Consulta IDSE (.txt)
            </Button>
            {lastLayoutPath ? (
              <p className="text-[11px] font-mono break-all bg-slate-50 border rounded p-2 m-0">{lastLayoutPath}</p>
            ) : null}

            <div className="space-y-2 pt-2 border-t border-slate-100">
              <Label className="text-xs">Procesar Respuesta IDSE / Archivo EMA (.txt)</Label>
              <Input
                type="file"
                accept=".txt"
                disabled={!clientId || busy}
                onChange={(e) => setRespuestaFile(e.target.files?.[0] || null)}
                className="text-xs"
              />
              <Button
                type="button"
                variant="outline"
                disabled={busy || !clientId || !respuestaFile}
                onClick={onProcesarRespuesta}
                className="w-full h-11 justify-start font-semibold border-emerald-700 text-emerald-800"
                data-testid="btn-procesar-respuesta-idse"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (
                  <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-emerald-100 text-xs mr-2">2</span>
                )}
                <FileUp className="w-4 h-4 mr-2" />
                Procesar Respuesta IDSE / Archivo EMA (.txt)
              </Button>
            </div>
          </div>

          {/* Sección 3 — Grid resultados */}
          <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b text-xs font-semibold text-[#0F2D5C]">
              3. Resultados
              {resultados ? (
                <span className="font-normal text-slate-600 ml-2">
                  — {resultados.localized_count} localizados / {resultados.not_found_count} no localizados
                </span>
              ) : null}
            </div>
            <div className="overflow-x-auto max-h-56">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>CURP</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>NSS</TableHead>
                    <TableHead>Estatus</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gridRows.length ? gridRows.map((row) => (
                    <TableRow
                      key={`${row.curp || row.id}-${row.consecutivo || ""}`}
                      className={
                        row.estatus === "localizado" || row.validacion_nss_status === "localizado"
                          ? "bg-emerald-50/80"
                          : row.estatus === "no_localizado" || row.validacion_nss_status === "no_localizado"
                            ? "bg-red-50/80"
                            : ""
                      }
                    >
                      <TableCell className="font-mono text-[10px]">{row.curp || "—"}</TableCell>
                      <TableCell className="text-xs max-w-[100px] truncate">{row.nombre_completo || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{row.nss || "—"}</TableCell>
                      <TableCell>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${nssBadge(row.estatus || row.validacion_nss_status)}`}>
                          {nssLabel(row.estatus || row.validacion_nss_status)}
                        </span>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-xs text-slate-500 text-center py-6">
                        Procese una respuesta IDSE o seleccione trabajadores con NSS ya registrado.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="px-3 py-2 border-t border-slate-100 bg-white flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={exporting || !clientId}
                    className="text-xs"
                    data-testid="btn-exportar-estatus-nss"
                  >
                    {exporting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <span className="mr-2" aria-hidden>📄</span>
                    )}
                    Exportar Estatus de NSS
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="text-xs">
                  <DropdownMenuItem
                    disabled={exporting}
                    onClick={() => onExportarNss("xlsx")}
                    data-testid="export-nss-excel"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Exportar a Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={exporting}
                    onClick={() => onExportarNss("pdf")}
                    data-testid="export-nss-pdf"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Exportar a PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Plan B — portal público */}
          <Collapsible open={planBOpen} onOpenChange={(open) => {
            setPlanBOpen(open);
            setPortalMode(open ? "publico" : "idse");
          }}>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-between text-xs text-slate-600 border border-dashed border-slate-300 h-10"
                data-testid="btn-plan-b-nss"
              >
                <span className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Plan B: Localización individual por Portal Público (Requiere CURP y Correo)
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${planBOpen ? "rotate-180" : ""}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 bg-white rounded-md border border-amber-200 p-4 space-y-3">
              <p className="text-[11px] text-amber-900 m-0 leading-relaxed">{notaPlanB}</p>
              {selected ? (
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => copyText(selected.curp, "CURP")}>
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copiar CURP
                  </Button>
                  <IdentityValidateButton curp={selected.curp} label="Validar CURP" />
                  <Button type="button" variant="outline" size="sm" onClick={() => copyText(selected.correo, "Correo")}>
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copiar Correo
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-slate-500 m-0">Seleccione un trabajador individual para copiar CURP y correo.</p>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Panel derecho — guía IDSE / portal público (sin iframe) */}
        <PortalExternoGuiaPanel
          title={portalMode === "publico" ? "IMSS Digital — Portal Público NSS" : "IDSE — Acceso patronal IMSS"}
          portalUrl={portalUrl}
          badge={portalMode === "publico" ? "Plan B — Portal público" : "IDSE — Certificado patronal"}
          intro={
            portalMode === "publico"
              ? notaPlanB || "Use solo si IDSE no está disponible. El correo debe coincidir con el registro histórico del trabajador."
              : "Flujo recomendado: certificado .cer/.key del registro patronal y archivo .txt de consulta generado en Visor Fiscal."
          }
          steps={portalGuiaSteps}
          openButtonLabel={
            portalMode === "publico"
              ? "Abrir portal público IMSS (NSS)"
              : "Abrir IDSE — IMSS"
          }
          testId="btn-abrir-portal-nss-imss"
          footerNote={
            portalMode === "publico"
              ? "Portal público: requiere CURP y correo histórico del trabajador."
              : "IDSE: firme con certificado patronal y cargue el .txt generado en el paso 1."
          }
        />
      </div>
      </LaboralFlujoOperativo>
    </div>
  );
}
